/**
 * Per-game play-time rules (the tweakable knobs).
 *
 * A single typed, frozen {@link GameRules} object holds the small set of rules a
 * game may vary — clue/answer timing, early-buzz handling (FR-4.3, explicitly
 * "configurable"), negative-score policy (FR-4.7), max seats (FR-2.5). It is
 * resolved ONCE at game creation by {@link resolveGameRules} (engine defaults →
 * pack defaults → per-game overrides) and then consumed everywhere, so
 * customization never spreads as scattered `if (option?.x)` branches or magic
 * numbers through the engine.
 *
 * This lives in `shared` (not game-server) deliberately: the control-plane
 * resolves rules when it creates a game (DESIGN §5.1), the engine consumes them,
 * and they are part of the persisted {@link GameState} so recovery (M4, DESIGN
 * §3.3) rebuilds identical play-time behavior.
 *
 * NOT to be confused with {@link PACK_RULES} in `pack.ts`: those are *structural
 * validation invariants* (categories/round, clues/category, Daily Double counts)
 * checked at upload time. `GameRules` is *play-time behavior*. Different
 * lifecycle, different consumers — kept separate on purpose. The only bridge is
 * that `maxPlayers` is bounded by `PACK_RULES.maxPlayersCeiling`.
 *
 * Deliberately a plain typed object with one resolver — no rules engine, no DSL.
 * New knobs are added one milestone at a time (wager bounds in M3, etc.).
 */

import type { Pack } from './pack';
import { PACK_RULES } from './pack';

/**
 * How a buzz that arrives before the buzzer window opens is handled (FR-4.3,
 * FR-4.11, DESIGN §6 step 5).
 *   - `ignore`: the early buzz simply does not win; no penalty.
 *   - `lockout`: the early buzzer forfeits this clue on the FIRST early buzz
 *     (added to its per-clue lockout list, server-side), matching real-Jeopardy
 *     "don't jump the gun" feel.
 *   - `smash-lockout`: anti-cheat "button locking" (FR-4.11) — enforced entirely
 *     CLIENT-SIDE. The player's buzzer stays tappable during the pre-open "Wait…"
 *     window (a disabled button can't be smashed — it swallows the events); the
 *     client counts each early smash and locks that player out of the clue once the
 *     count reaches {@link GameRules.earlyBuzzSmashLimit}. Tolerates a couple of
 *     accidental taps but punishes sustained smashing. The host opts in at game
 *     creation; the server treats it like `ignore` (no server-side bookkeeping).
 */
export type EarlyBuzzPolicy = 'ignore' | 'lockout' | 'smash-lockout';

/**
 * Fully-resolved, play-time game rules. Frozen after {@link resolveGameRules};
 * stored on {@link GameState} so it snapshots + recovers with the game (M4).
 */
export interface GameRules {
  /** Clue-timer fallback (seconds) when a clue has no `timerDuration` (FR-7.5). */
  defaultTimerSec: number;
  /** Answer window (seconds) a buzzed-in player has before auto-incorrect (FR-4.4). */
  answerWindowSec: number;
  /** Early-buzz handling (FR-4.3): ignore, forfeit on first, or smash-lockout. */
  earlyBuzzPolicy: EarlyBuzzPolicy;
  /**
   * Anti-cheat buzzer-smash threshold (FR-4.11): the number of early "Wait…"-window
   * smashes a player may make for a single clue before `earlyBuzzPolicy:
   * 'smash-lockout'` locks them out of that clue. Default `3` — tolerate two
   * accidental taps, lock out on the third. Only consulted when `earlyBuzzPolicy ===
   * 'smash-lockout'`, and consumed CLIENT-SIDE (the client enforces the lockout).
   * Must be ≥ 1; the tweakable home for the constant (never a magic number).
   */
  earlyBuzzSmashLimit: number;
  /**
   * If `false`, a wrong answer floors the player's score at 0 instead of going
   * negative. Default `true` (negatives allowed — FR-4.7).
   */
  allowNegativeScores: boolean;
  /** Effective max seats (≤ {@link PACK_RULES.maxPlayersCeiling}, FR-2.5). */
  maxPlayers: number;
  /**
   * Daily Double minimum wager (FR-5.3). Real Jeopardy floors DD wagers at $5.
   * The tweakable home for the "$5" constant — never scatter it in the actor.
   */
  dailyDoubleMinWager: number;
  /**
   * Daily Double max-wager floor (FR-5.3): the allowed maximum is
   * `max(dailyDoubleMaxFloor, playerScore)` so a player with a small/negative
   * score may still wager up to this floor. The tweakable home for the "$2000".
   */
  dailyDoubleMaxFloor: number;
  /**
   * Final Jeopardy minimum wager (FR-6.2). Default $0 — a player may stand pat.
   * The maximum is the player's current score (a score ≤ 0 → max $0).
   */
  finalJeopardyMinWager: number;
  /**
   * Final Jeopardy WAGER window (seconds). Unlike a board clue, Final wagering is a
   * deliberate, private decision, so it is decoupled from `answerWindowSec` (which
   * is board-clue timing and is often set low for a snappy demo — that short value
   * was previously bleeding into Final and rushing wagers).
   *
   * **`0` means UNLIMITED** — no wager timer is armed, and the phase advances only
   * when every participant has wagered. Default `0`. NOTE: with an unlimited window
   * there is no timer-based no-stall net, so a participant who never wagers holds
   * the phase open; set a positive value to re-arm the auto-advance safety window.
   */
  finalJeopardyWagerSec: number;
  /**
   * Final Jeopardy ANSWER window (seconds) — how long players have to type their
   * secret response once the prompt is revealed (FR-6.3). Decoupled from the
   * board-clue `answerWindowSec` for the same reason as the wager window. Default
   * `60` (a real Final Jeopardy gives ~30s of think time plus reading; 60s is
   * comfortable for typing). `0` would mean unlimited, but a bounded answer window
   * is expected so judging can proceed.
   */
  finalJeopardyAnswerSec: number;
  /**
   * Auto-host (#91, D1) — extra padding (seconds) added to the estimated
   * read-aloud duration before the buzzer auto-opens, so a slightly-slow local
   * voice isn't cut off mid-word. The buzzer opens at
   * `min(all clients read_complete, estimate + this)`, floored so it never opens
   * before the read could plausibly finish. Ignored in `HUMAN` mode.
   */
  autoHostReadBufferSec: number;
  /**
   * Auto-host (#91, D8) — the Dispute vote window (seconds): how long the other
   * players have to vote on a disputed ruling before it auto-resolves (partial
   * vote → majority of cast, tie/none → ruling stands). Ignored in `HUMAN` mode.
   */
  disputeVoteSec: number;
  /**
   * End-of-clue reveal hold (seconds) for an UNANSWERED clue (#192, FR-4.10): how
   * long the correct response is held on the player + presentation surfaces before
   * the board returns (all-wrong, timer-expiry, dismiss, or a wrong Daily Double).
   * Default `3` (owner sign-off on #192; long enough to read a short response). `0`
   * would skip the hold and return immediately.
   */
  revealHoldSec: number;
  /**
   * Award hold (seconds) after a CORRECT answer (#192, FR-4.10): a brief pause on
   * the award cue (green flash + "✓ +$value" + scoreboard pulse) before board
   * control passes to the correct player. The response is NOT shown/read. Default
   * `1.5`. `0` returns to the board immediately (a pure client-side pulse).
   */
  awardHoldSec: number;
  /**
   * Final Peril correct-response reveal hold (seconds) — #205, FR-6.6. The Final
   * analogue of {@link revealHoldSec}: after the LAST player is judged, the game
   * lingers on the judged state (with the correct response revealed to all roles)
   * for this long — a dedicated `FINAL_REVEAL` phase — before advancing to
   * GAME_OVER. Default `5` (owner sign-off on #205): deliberately longer than a
   * board clue because it is the final question. In AUTO mode the hold is stretched
   * to at least the estimated answer read-aloud time (+ `autoHostReadBufferSec`) so
   * the spoken response isn't cut off. `0` would advance immediately.
   */
  finalRevealHoldSec: number;
  /**
   * Max categories a single player may submit for dynamic generation (#203, FR-11.2).
   * A per-player anti-dominance ceiling on the lobby `set_category_preferences` wish
   * list; the actor sanitizes each submission to at most this many. Fairness across
   * players is delivered by the round-robin draft (`fairCategoryOrder`), so this is the
   * hard input bound, not the balancing mechanism. Default `12`. The tweakable home for
   * the constant — never scatter a magic number in the actor.
   */
  maxCategoryPreferencesPerPlayer: number;
  /**
   * Per-game override for the number of Daily Doubles ("Hidden Perils") placed in the
   * JEOPARDY (round-1) board (#329, FR-5.1). **Optional and default-absent** — unlike
   * every other field here, an absent value is meaningful: it means "keep the pack /
   * generator's own DD placement" (the default). When set (0..30), the board is
   * RELABELED at creation by {@link applyDailyDoubleCounts} — an arbitrary set of clues
   * is flagged, with NO regeneration — so a custom count never triggers live generation.
   * Bounded to `[0, PACK_RULES.categoriesPerRound * PACK_RULES.cluesPerCategory]` (= 30)
   * by the server resolver; the pack-level structural invariant ({@link PACK_RULES}) is
   * unchanged and still validated at upload.
   */
  dailyDoubleCountJeopardy?: number;
  /**
   * Per-game override for the number of Daily Doubles in the DOUBLE_JEOPARDY (round-2)
   * board (#329, FR-5.1). Same semantics + bounds as {@link dailyDoubleCountJeopardy}:
   * optional/default-absent (absent ⇒ keep authored/generated placement), relabel-only,
   * no regeneration.
   */
  dailyDoubleCountDoubleJeopardy?: number;
}

/** Per-game overrides supplied at game creation (control-plane). All optional. */
export type GameRulesOverrides = Partial<GameRules>;

/**
 * Hardcoded engine defaults — the single home for the play-time rule constants.
 * Every field is overridable by the pack layer or a per-game override.
 */
export const DEFAULT_GAME_RULES: GameRules = {
  defaultTimerSec: 15,
  answerWindowSec: 15,
  earlyBuzzPolicy: 'ignore',
  // Anti-cheat buzzer-smash lockout (FR-4.11): tolerate two accidental early taps,
  // lock the smasher out of the clue on the third. Only used under 'smash-lockout'.
  earlyBuzzSmashLimit: 3,
  allowNegativeScores: true,
  maxPlayers: PACK_RULES.maxPlayersCeiling,
  dailyDoubleMinWager: 5,
  dailyDoubleMaxFloor: 2000,
  finalJeopardyMinWager: 0,
  // Final wagering is unlimited by default (0); answering is a comfortable 60s.
  // Both are deliberately independent of the board-clue timer so a fast demo clue
  // timer never rushes the Final round again.
  finalJeopardyWagerSec: 0,
  finalJeopardyAnswerSec: 60,
  // Auto-host (#91): a generous read buffer so a slow browser voice isn't clipped,
  // and a 20s window for the other players to vote on a disputed ruling.
  autoHostReadBufferSec: 2,
  disputeVoteSec: 20,
  // End-of-clue reveal (#192): hold the correct response 3s on an unanswered clue,
  // and pause 1.5s on the award cue before board control passes on a correct answer.
  revealHoldSec: 3,
  awardHoldSec: 1.5,
  // Final Peril reveal (#205, FR-6.6): linger 5s on the judged state (correct
  // response revealed to all) after the last player is judged, before GAME_OVER —
  // longer than a board clue's 3s because it is the final question. AUTO stretches
  // it to fit the spoken answer.
  finalRevealHoldSec: 5,
  // Dynamic category balancing (#203): cap each player at 12 submitted wishes; the
  // round-robin draft does the cross-player fairness.
  maxCategoryPreferencesPerPlayer: 12,
};

/**
 * Resolve the effective {@link GameRules} for a game. Single precedence chain:
 *
 *   hardcoded engine defaults → pack defaults → per-game overrides
 *
 * Timer knobs (`defaultTimerSec`/`answerWindowSec`) always originate at the pack
 * layer: `pack.defaultTimerDuration` is required, so the engine constants for
 * those two fields are documentation anchors, not a runtime fallback (a per-game
 * override still wins). The engine defaults do fill knobs the pack has no field
 * for (`earlyBuzzPolicy`, `allowNegativeScores`).
 *
 * (Per-clue `timerDuration` is clue data, resolved at the use site against
 * `rules.defaultTimerSec` — it is intentionally NOT part of this object.)
 *
 * Returns a frozen object so no consumer can mutate a game's rules after
 * creation. Call this ONCE at game creation, never inside the actor's hot path.
 * NOTE: freezing is an in-memory, creation-time guarantee; a snapshot round-trip
 * (M4 recovery) yields a plain object, so recovery must re-resolve or re-freeze.
 */
export function resolveGameRules(
  pack: Pack | undefined,
  overrides?: GameRulesOverrides,
): GameRules {
  const merged: GameRules = {
    ...DEFAULT_GAME_RULES,
    // Pack layer: the authored defaults (wires the FR-7.5 pack fallback). A DYNAMIC
    // game (M12) has no pack at creation, so it resolves from engine defaults +
    // overrides only; the generated pack's timing lands via the snapshot swap.
    ...(pack
      ? {
          defaultTimerSec: pack.defaultTimerDuration,
          answerWindowSec: pack.defaultTimerDuration,
          maxPlayers: pack.maxPlayers,
        }
      : {}),
    // Per-game overrides win last.
    ...overrides,
  };
  // Clamp AFTER overrides so a per-game override can never exceed the structural
  // ceiling (FR-2.5). Must be the last word — a raw `...overrides` spread would
  // otherwise reintroduce an unbounded maxPlayers.
  merged.maxPlayers = Math.min(merged.maxPlayers, PACK_RULES.maxPlayersCeiling);
  return Object.freeze(merged);
}

/** An inclusive `[min, max]` wager range. */
export interface WagerBounds {
  min: number;
  max: number;
}

/**
 * Daily Double wager bounds for a player with `score` (FR-5.3, rule nuance #5):
 * `[dailyDoubleMinWager, max(dailyDoubleMaxFloor, score)]`. A player whose score
 * is below the floor may still wager up to the floor; a high scorer may wager up
 * to their whole score. Pure — the single home for the DD-bound math, reused by
 * server validation and the client wager input.
 */
export function dailyDoubleWagerBounds(rules: GameRules, score: number): WagerBounds {
  return {
    min: rules.dailyDoubleMinWager,
    max: Math.max(rules.dailyDoubleMaxFloor, score),
  };
}

/**
 * Final Jeopardy wager bounds for a player with `score` (FR-6.2):
 * `[finalJeopardyMinWager, max(finalJeopardyMinWager, score)]`. A player with a
 * score ≤ 0 may only wager the minimum (default $0 — they cannot risk more than
 * they have). Pure — reused by server validation and the client wager input.
 */
export function finalJeopardyWagerBounds(rules: GameRules, score: number): WagerBounds {
  return {
    min: rules.finalJeopardyMinWager,
    max: Math.max(rules.finalJeopardyMinWager, score),
  };
}

/**
 * True if `amount` is a whole-number wager within the inclusive `bounds`.
 * Wagers must be integers (no fractional dollars) and finite. Server-side
 * validation (NFR-5.3) rejects anything else.
 */
export function isWagerInBounds(amount: number, bounds: WagerBounds): boolean {
  return Number.isInteger(amount) && amount >= bounds.min && amount <= bounds.max;
}
